import React, { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Box,
  Typography,
  Grid,
  Chip,
  Stack
} from '@mui/material'
import { useGlobalStoreData } from '../../store/globalStore'
import { countryEN } from '../../assets/data/country-en'
import { countryTR } from '../../assets/data/country-tr'
import { TRADE_TYPES, LANGUAGES } from '../../mailTemplate/src/utils/campaignConstants'
import {
  Category,
  Description,
  Business,
  Person,
  Email,
  Language,
  Public,
  ShoppingCart,
  Phone,
  Web,
  LocationOn,
  ProductionQuantityLimits,
  Inventory,
  TrendingUp,
  Stars,
  Campaign,
  Code,
  Flag
} from '@mui/icons-material'

const DetailItem = ({ icon, label, value }) => (
  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, py: 1.5 }}>
    <Box sx={{ color: 'primary.main', mt: 0.5, opacity: 0.7 }}>
      {icon}
    </Box>
    <Box sx={{ flex: 1 }}>
      <Typography
        variant="caption"
        sx={{
          color: 'text.secondary',
          fontSize: '0.75rem',
          fontWeight: 500,
          mb: 0.25
        }}
      >
        {label}
      </Typography>
      <Typography
        component="div"
        variant="body2"
        sx={{
          color: 'text.primary',
          fontWeight: 400,
          fontSize: '0.875rem'
        }}
      >
        {value || '-'}
      </Typography>
    </Box>
  </Box>
)

const CampaignDetailInfo = ({ campaign }) => {
  const { t } = useTranslation()

  const languageGSD = useGlobalStoreData.language()

  const getTranslatedTradeType = useCallback((tradeType) => {
    if (!tradeType) return ''

    switch (tradeType) {
      case TRADE_TYPES.EXPORT:
        return t('mailTemplate.wizard.ai.export')
      case TRADE_TYPES.IMPORT:
        return t('mailTemplate.wizard.ai.import')
      default:
        return tradeType
    }
  }, [t])

  const getTranslatedLanguage = useCallback((language) => {
    if (!language) return ''

    switch (language) {
      case LANGUAGES.ENGLISH:
        return t('mailTemplate.wizard.ai.languages.english')
      case LANGUAGES.TURKISH:
        return t('mailTemplate.wizard.ai.languages.turkish')
      default:
        return language
    }
  }, [t])

  const getLocalizedCountryName = useCallback((countryValue) => {
    if (!countryValue) return ''

    const currentCountries = languageGSD === 'en' ? countryEN : countryTR
    const otherCountries = languageGSD === 'en' ? countryTR : countryEN

    const countryByCode = currentCountries.find(c => c.countryCode === countryValue)
    if (countryByCode) {
      return countryByCode.countryName
    }

    const foundInCurrent = currentCountries.find(c => c.countryName === countryValue)
    if (foundInCurrent) {
      return countryValue
    }

    const foundInOther = otherCountries.find(c => c.countryName === countryValue)
    if (foundInOther) {
      const translatedCountry = currentCountries.find(c => c.countryCode === foundInOther.countryCode)
      if (translatedCountry) {
        return translatedCountry.countryName
      }
    }

    return countryValue
  }, [languageGSD])

  const renderArrayItems = (items) => {
    if (!items) return '-'

    if (typeof items === 'string') {
      return items
    }

    if (!Array.isArray(items) || items.length === 0) return '-'

    return (
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
        {items.map((item, index) => (
          <Chip
            key={index}
            label={item}
            size="small"
            sx={{
              backgroundColor: '#f5f5f5',
              color: 'text.secondary',
              fontWeight: 500,
              border: '1px solid #e0e0e0'
            }}
          />
        ))}
      </Stack>
    )
  }

  return (
    <Box sx={{ width: '100%', p: 2 }}>

      {/* Campaign Basic Info Section */}
      <Box sx={{ mb: 4 }}>
        <Typography
          variant="h6"
          sx={{
            fontWeight: 600,
            mb: 3,
            color: 'primary.main',
            fontSize: '1.1rem'
          }}
        >
          {t('mailTemplate.campaigns.modal.campaignInformation')}
        </Typography>

        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <DetailItem
              icon={<Campaign />}
              label={t('mailTemplate.campaigns.modal.name')}
              value={campaign?.name}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <DetailItem
              icon={<Code />}
              label={t('mailTemplate.campaigns.modal.hsCodes')}
              value={renderArrayItems(campaign?.hsCodes)}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <DetailItem
              icon={<Flag />}
              label={t('mailTemplate.campaigns.modal.importerCountryCode')}
              value={campaign?.importerCountryCodes?.map(code => getLocalizedCountryName(code)).join(', ')}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <DetailItem
              icon={<Flag />}
              label={t('mailTemplate.campaigns.modal.currentCountryCode')}
              value={getLocalizedCountryName(campaign?.currentCountryCode)}
            />
          </Grid>
          <Grid item xs={12}>
            <DetailItem
              icon={<Description />}
              label={t('mailTemplate.campaigns.modal.productName')}
              value={renderArrayItems(campaign?.productName)}
            />
          </Grid>
        </Grid>
      </Box>

      {/* Sender Company Section */}
      {campaign?.sender_company && (
        <Box sx={{ mb: 4 }}>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              mb: 3,
              color: 'primary.main',
              fontSize: '1.1rem'
            }}
          >
            {t('mailTemplate.campaigns.detail.senderCompany')}
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <DetailItem
                icon={<Business />}
                label={t('mailTemplate.campaigns.detail.companyName')}
                value={campaign?.sender_company?.name}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <DetailItem
                icon={<Category />}
                label={t('mailTemplate.campaigns.detail.industry')}
                value={campaign?.sender_company?.industry}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <DetailItem
                icon={<Public />}
                label={t('mailTemplate.campaigns.detail.country')}
                value={getLocalizedCountryName(campaign?.sender_company?.country)}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <DetailItem
                icon={<Person />}
                label={t('mailTemplate.campaigns.detail.contactPerson')}
                value={campaign?.sender_company?.contact_person}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <DetailItem
                icon={<Person />}
                label={t('mailTemplate.campaigns.detail.contactTitle')}
                value={campaign?.sender_company?.contact_title}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <DetailItem
                icon={<Email />}
                label={t('mailTemplate.campaigns.detail.email')}
                value={campaign?.sender_company?.email}
              />
            </Grid>
            {campaign?.sender_company?.website && (
              <Grid item xs={12} md={6}>
                <DetailItem
                  icon={<Web />}
                  label={t('mailTemplate.campaigns.detail.website')}
                  value={campaign?.sender_company?.website}
                />
              </Grid>
            )}
            {campaign?.sender_company?.phone && (
              <Grid item xs={12} md={6}>
                <DetailItem
                  icon={<Phone />}
                  label={t('mailTemplate.campaigns.detail.phone')}
                  value={campaign?.sender_company?.phone}
                />
              </Grid>
            )}
            {campaign?.sender_company?.production_capacity && (
              <Grid item xs={12}>
                <DetailItem
                  icon={<ProductionQuantityLimits />}
                  label={t('mailTemplate.campaigns.detail.productionCapacity')}
                  value={campaign?.sender_company?.production_capacity}
                />
              </Grid>
            )}
            {campaign?.sender_company?.key_products?.length > 0 && (
              <Grid item xs={12} md={6}>
                <DetailItem
                  icon={<ShoppingCart />}
                  label={t('mailTemplate.campaigns.detail.keyProducts')}
                  value={renderArrayItems(campaign?.sender_company?.key_products)}
                />
              </Grid>
            )}
            {campaign?.sender_company?.facilities?.length > 0 && (
              <Grid item xs={12} md={6}>
                <DetailItem
                  icon={<LocationOn />}
                  label={t('mailTemplate.campaigns.detail.facilities')}
                  value={renderArrayItems(campaign?.sender_company?.facilities)}
                />
              </Grid>
            )}
            {campaign?.sender_company?.certifications?.length > 0 && (
              <Grid item xs={12} md={6}>
                <DetailItem
                  icon={<Inventory />}
                  label={t('mailTemplate.campaigns.detail.certifications')}
                  value={renderArrayItems(campaign?.sender_company?.certifications)}
                />
              </Grid>
            )}
            {campaign?.sender_company?.market_experience?.length > 0 && (
              <Grid item xs={12} md={6}>
                <DetailItem
                  icon={<TrendingUp />}
                  label={t('mailTemplate.campaigns.detail.marketExperience')}
                  value={renderArrayItems(campaign?.sender_company?.market_experience)}
                />
              </Grid>
            )}
            {campaign?.sender_company?.company_values?.length > 0 && (
              <Grid item xs={12} md={6}>
                <DetailItem
                  icon={<Stars />}
                  label={t('mailTemplate.campaigns.detail.companyValues')}
                  value={renderArrayItems(campaign?.sender_company?.company_values)}
                />
              </Grid>
            )}
          </Grid>
        </Box>
      )}

      {/* Email Purpose Section */}
      {campaign?.email_purpose && (
        <Box>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              mb: 3,
              color: 'primary.main',
              fontSize: '1.1rem'
            }}
          >
            {t('mailTemplate.campaigns.detail.emailPurpose')}
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <DetailItem
                icon={<Public />}
                label={t('mailTemplate.campaigns.detail.targetMarket')}
                value={getLocalizedCountryName(campaign?.email_purpose?.target_market)}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <DetailItem
                icon={<Business />}
                label={t('mailTemplate.campaigns.detail.tradeType')}
                value={getTranslatedTradeType(campaign?.email_purpose?.trade_type)}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <DetailItem
                icon={<Category />}
                label={t('mailTemplate.campaigns.detail.productCategory')}
                value={campaign?.email_purpose?.product_category}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <DetailItem
                icon={<Language />}
                label={t('mailTemplate.campaigns.detail.language')}
                value={getTranslatedLanguage(campaign?.email_purpose?.language)}
              />
            </Grid>
            {campaign?.email_purpose?.call_to_action && (
              <Grid item xs={12}>
                <DetailItem
                  icon={<Description />}
                  label={t('mailTemplate.campaigns.detail.callToAction')}
                  value={campaign?.email_purpose?.call_to_action}
                />
              </Grid>
            )}
            {campaign?.email_purpose?.specific_products?.length > 0 && (
              <Grid item xs={12}>
                <DetailItem
                  icon={<ShoppingCart />}
                  label={t('mailTemplate.campaigns.detail.specificProducts')}
                  value={renderArrayItems(campaign?.email_purpose?.specific_products)}
                />
              </Grid>
            )}
          </Grid>
        </Box>
      )}
    </Box>
  )
}

export default CampaignDetailInfo
